import React, { useEffect, useRef, useCallback } from "react";
import Input from "@mui/joy/Input";

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  size?: "sm" | "md" | "lg";
  [key: string]: any; // Allow other Input props to be passed through
}

/**
 * DateInput component that handles empty state styling for date inputs.
 *
 * Features:
 * - Shows faint placeholder text when completely empty AND not focused
 * - Uses normal text color in all other cases (focused empty, partial dates, complete dates)
 * - Automatically manages data-empty attribute
 * - Works with global CSS in index.css for consistent styling
 */
const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  required = false,
  size,
  ...otherProps
}) => {
  // Use callback ref to set data-empty attribute
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputRefCallback = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (node) {
      const currentValue = node.value || "";
      const isValueEmpty = !currentValue || currentValue.trim() === "";

      // Only set data-empty when completely empty
      if (isValueEmpty) {
        node.setAttribute("data-empty", "true");
      } else {
        node.removeAttribute("data-empty");
      }
    }
  }, []);

  // Update data-empty attribute when value changes
  useEffect(() => {
    if (inputRef.current) {
      const currentValue = inputRef.current.value || value || "";
      const isValueEmpty = !currentValue || currentValue.trim() === "";

      if (isValueEmpty) {
        inputRef.current.setAttribute("data-empty", "true");
      } else {
        inputRef.current.removeAttribute("data-empty");
      }
    }
  }, [value]);

  // Handle change events
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  // Handle input events to update data-empty in real-time
  const handleInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const currentValue = target.value || "";
    const isValueEmpty = !currentValue || currentValue.trim() === "";

    // Update data-empty based on whether value is completely empty
    if (isValueEmpty) {
      target.setAttribute("data-empty", "true");
    } else {
      target.removeAttribute("data-empty");
    }
  }, []);

  // Handle blur events
  const handleBlur = useCallback(() => {
    // Re-evaluate data-empty based on actual value
    if (inputRef.current) {
      const finalValue = inputRef.current.value || "";
      const isValueEmpty = !finalValue || finalValue.trim() === "";

      if (isValueEmpty) {
        inputRef.current.setAttribute("data-empty", "true");
      } else {
        inputRef.current.removeAttribute("data-empty");
      }
    }
  }, []);

  return (
    <Input
      type="date"
      value={value || ""}
      onChange={handleChange}
      required={required}
      size={size}
      {...otherProps}
      sx={{
        ...otherProps.sx, // Allow additional sx props to override
      }}
      slotProps={{
        input: {
          ref: inputRefCallback,
          onInput: handleInput,
          onBlur: handleBlur,
          ...otherProps.slotProps?.input, // Allow additional input slot props
        },
        ...otherProps.slotProps, // Allow other slot props
      }}
    />
  );
};

export default DateInput;
